// SCR-STAFF-712 و SCR-STAFF-714 (AP-MED-04؛ US-MED-089، 090): پیگیری تهیه‌کننده و صندوق تأیید از راه دور استاد.
// صندوق استاد **موبایل‌محور** است (همان PWA اپ کارکنان) و هر مشاهده در سرور با RecordView ثبت می‌شود (AC-MED-090.2).
// تهیه‌کننده در هیچ جای این صفحه‌ها اعتبارنامه، توکن، OTP، QR یا شماره همراه استاد را نمی‌بیند (AC-MED-089.2).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type MedPrescriptionApprovalView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, ListPage, StatusChip, toJalaliDateTime, useDisplaySettings, type DataTableColumn, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { highVolumeKeys, SCR_STAFF_712, SCR_STAFF_714 } from './highVolume';

function tone(status: MedPrescriptionApprovalView['status']): StatusTone {
  switch (status) {
    case 'Approved':
      return 'done';
    case 'Rejected':
      return 'error';
    case 'Expired':
    case 'Recalled':
      return 'cancelled';
    default:
      return 'pending';
  }
}

/** SCR-STAFF-712: پیش‌نویس‌های ارسالی تهیه‌کننده با وضعیت، کد رهگیری و دلیل رد (AC-MED-089.3). */
export function MyApprovalsPage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();

  const rows = useQuery({
    queryKey: highVolumeKeys.mine(session?.tenantId),
    enabled: Boolean(session?.tenantId) && access.can('med.prescription.request_approval'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/approvals/mine')),
  });

  const recall = useMutation({
    mutationFn: (approvalId: string) => unwrap(tenantApi.POST('/api/v1/medical-orders/approvals/{approvalId}:recall', { params: { path: { approvalId } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: highVolumeKeys.mine(session?.tenantId) }),
  });

  const columns: DataTableColumn<MedPrescriptionApprovalView>[] = [
    { id: 'status', headerKey: 'med:approval.status', cell: (row) => <StatusChip tone={tone(row.status)} labelKey={`med:approval_status.${row.status}`} /> },
    { id: 'approver', headerKey: 'med:approval.approver', cell: (row) => <span>{row.approverName ?? '—'}</span> },
    { id: 'requested', headerKey: 'med:approval.requested_at', cell: (row) => <span>{toJalaliDateTime(row.requestedAt, { timeZone })}</span> },
    { id: 'expires', headerKey: 'med:approval.expires_at', cell: (row) => <span>{toJalaliDateTime(row.expiresAt, { timeZone })}</span> },
    {
      id: 'tracking',
      headerKey: 'med:approval.tracking_code',
      cell: (row) => <span data-testid={`med-approval-tracking-${row.approvalId}`}>{row.trackingCode ?? '—'}</span>,
    },
    {
      id: 'reason',
      headerKey: 'med:approval.reject_reason',
      cell: (row) => <span data-testid={`med-approval-reason-${row.approvalId}`}>{row.rejectReason ?? '—'}</span>,
    },
    {
      id: 'actions',
      headerKey: 'med:approval.actions',
      cell: (row) =>
        row.status === 'Pending' ? (
          <Button size="small" variant="outlined" onClick={() => recall.mutate(row.approvalId)} data-testid={`med-approval-recall-${row.approvalId}`}>
            {t('med:approval.recall')}
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage screenId={SCR_STAFF_712} titleKey="med:approval.mine_title" subtitleKey="med:approval.mine_subtitle">
      <DataTable
        rows={rows.data?.items}
        columns={columns}
        getRowId={(row) => row.approvalId}
        isLoading={rows.isLoading}
        error={rows.error}
        onRetry={() => void rows.refetch()}
        emptyTitleKey="med:approval.mine_empty"
        captionKey="med:approval.mine_title"
      />
    </ListPage>
  );
}

/**
 * SCR-STAFF-714: صندوق تأیید استاد — قدیمی‌ترین اول، با نام و سن مراجعه‌کننده، تهیه‌کننده، اقلام با دوز و دستور مصرف و
 * هشدارهای ایمنی (AC-MED-090.2). «تأیید، امضا و ارسال» با PIN خودِ استاد انجام می‌شود (AC-MED-090.3).
 */
export function ApprovalInboxPage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [dialog, setDialog] = useState<{ kind: 'approve' | 'reject'; row: MedPrescriptionApprovalView } | null>(null);
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [insurer, setInsurer] = useState('TAMIN');

  const rows = useQuery({
    queryKey: highVolumeKeys.inbox(session?.tenantId),
    enabled: Boolean(session?.tenantId) && access.can('med.prescription.approve_remote'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/approvals/inbox')),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: highVolumeKeys.inbox(session?.tenantId) });
  };

  const approve = useMutation({
    mutationFn: (row: MedPrescriptionApprovalView) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/approvals/{approvalId}:approve', {
          params: { path: { approvalId: row.approvalId } },
          body: { pin, submitNationally: true, insurer: insurer || null, earlyRefillReason: null, limitReason: null },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setDialog(null);
      setPin('');
      invalidate();
    },
  });

  const reject = useMutation({
    mutationFn: (row: MedPrescriptionApprovalView) =>
      unwrap(tenantApi.POST('/api/v1/medical-orders/approvals/{approvalId}:reject', { params: { path: { approvalId: row.approvalId } }, body: { reason } })),
    onSuccess: () => {
      setDialog(null);
      setReason('');
      invalidate();
    },
  });

  const error = approve.error ?? reject.error;
  const errorCode = isApiError(error) ? error.code : undefined;

  return (
    <ListPage screenId={SCR_STAFF_714} titleKey="med:approval.inbox_title" subtitleKey="med:approval.inbox_subtitle">
      <Stack spacing={2} data-testid="med-approval-inbox">
        {errorCode === 'PHX-MED-0326' ? (
          <Alert severity="warning" data-testid="med-approval-expired">
            {t('med:approval.expired_hint')}
          </Alert>
        ) : null}
        {(rows.data?.items ?? []).length === 0 && !rows.isLoading ? (
          <Alert severity="info" data-testid="med-approval-inbox-empty">
            {t('med:approval.inbox_empty')}
          </Alert>
        ) : null}
        {(rows.data?.items ?? []).map((row) => (
          <Card key={row.approvalId} variant="outlined" data-testid={`med-approval-card-${row.approvalId}`}>
            <CardContent>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Typography variant="subtitle1">{row.patientName ?? '—'}</Typography>
                  {row.patientAgeYears ? <Chip size="small" label={t('med:approval.age', { years: faDigits(row.patientAgeYears) })} /> : null}
                  <StatusChip tone={tone(row.status)} labelKey={`med:approval_status.${row.status}`} />
                  {row.hasCriticalAlert ? <Chip size="small" color="error" label={t('med:approval.critical')} data-testid={`med-approval-critical-${row.approvalId}`} /> : null}
                </Stack>
                <Typography variant="body2">{t('med:approval.requested_by', { name: row.requestedByName ?? '—' })}</Typography>
                <Typography variant="body2">{t('med:approval.expires_at')}: {toJalaliDateTime(row.expiresAt, { timeZone })}</Typography>
                <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }} data-testid={`med-approval-items-${row.approvalId}`}>
                  {(row.items ?? []).map((item) => (
                    <Typography component="li" variant="body2" key={item}>
                      {item}
                    </Typography>
                  ))}
                </Stack>
                {(row.alerts ?? []).length > 0 ? (
                  <Alert severity={row.hasCriticalAlert ? 'error' : 'warning'} data-testid={`med-approval-alerts-${row.approvalId}`}>
                    {(row.alerts ?? []).join(t('common:list_separator'))}
                  </Alert>
                ) : null}
              </Stack>
            </CardContent>
            <CardActions>
              <Button variant="contained" onClick={() => setDialog({ kind: 'approve', row })} data-testid={`med-approval-approve-${row.approvalId}`}>
                {t('med:approval.approve')}
              </Button>
              <Button variant="outlined" color="error" onClick={() => setDialog({ kind: 'reject', row })} data-testid={`med-approval-reject-${row.approvalId}`}>
                {t('med:approval.reject')}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Stack>
      {dialog ? (
        <FormDialog
          open
          titleKey={dialog.kind === 'approve' ? 'med:approval.approve' : 'med:approval.reject'}
          submitKey={dialog.kind === 'approve' ? 'med:approval.approve' : 'med:approval.reject'}
          screenId={SCR_STAFF_714}
          onSubmit={() => (dialog.kind === 'approve' ? approve.mutate(dialog.row) : reject.mutate(dialog.row))}
          onClose={() => setDialog(null)}
          isDirty={dialog.kind === 'approve' ? pin.length > 0 : reason.length > 0}
          isSubmitting={approve.isPending || reject.isPending}
          submitError={error}
        >
          {dialog.kind === 'approve' ? (
            <Stack spacing={1}>
              <TextField
                label={t('med:approval.pin')}
                type="password"
                value={pin}
                onChange={(event) => setPin(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 8, inputMode: 'numeric', 'data-testid': 'med-approval-pin' } }}
              />
              <TextField
                label={t('med:approval.insurer')}
                value={insurer}
                onChange={(event) => setInsurer(event.target.value.toUpperCase())}
                helperText={t('med:approval.insurer_hint')}
                slotProps={{ htmlInput: { maxLength: 20, 'data-testid': 'med-approval-insurer' } }}
              />
            </Stack>
          ) : (
            <TextField
              label={t('med:approval.reject_reason')}
              value={reason}
              multiline
              minRows={2}
              onChange={(event) => setReason(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'med-approval-reject-reason' } }}
            />
          )}
        </FormDialog>
      ) : null}
    </ListPage>
  );
}
